import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import 'devextreme/data/odata/store';
import { Observable, Subject, throwError } from 'rxjs';
import {retry, timeout} from 'rxjs/operators';
import {AlertEnum} from "../components/messages/messages.enum";
import {requestResponse} from "../models/service-response.model";
import {Security} from "../utils/helpers/security";
import { AppConfigService } from './app-config.service';
import {MessageService} from "../components/messages/message.service";
import { localStorageKeys } from "./system.service";
import { HeaderRequestEnum } from "../enums/header-request.enum";
import { ResponseStatusEnum } from "../enums/response-status.enum";

@Injectable()
export class AppService
{
    /**
     * Evento para solicitar cierre de sesión desde cualquier parte del sistema (ej. 401).
     * Importante: debe ser `Subject` (no `BehaviorSubject`) para evitar dispararse al suscribirse.
     */
    public logOut: Subject<void> = new Subject<void>();

    private readonly headerSecret: string = "Secret";
    private readonly headerNoSpinner: string = "ignoreLoadingBar";
    private readonly headerLanguage: string = "Language";

    public userUnauthorized: boolean = false;

    /** Timeout (ms) para no dejar peticiones colgadas cuando el servidor está caído o arrancando; libera conexiones. */
    private static readonly REQUEST_TIMEOUT_MS = 20000;

    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private messageService: MessageService)
    {
    }

    //-- Petición get
    get(url: string, requestHeaders: Array<HeaderRequestEnum> = []): Promise<requestResponse>
    {
        return new Promise<requestResponse>((resolve, reject) =>
        {
            let httpOptions = {headers: this.setHeader(requestHeaders), observe: 'response' as 'response'};

            this.http.get(url, httpOptions).pipe(
                timeout(AppService.REQUEST_TIMEOUT_MS)
            ).subscribe(
                {
                    next: (httpResponse: HttpResponse<any>) =>
                    {
                        let response = this.requestSuccess(httpResponse);

                        if (response != null)
                        {
                            resolve(response);
                        }
                        else
                        {
                            if (httpResponse.body?.message) {
                                reject(new Error(httpResponse.body.message));
                            } else {
                                this.rejectWithErrorConexion(reject);
                            }
                        }
                    },
                    error: (error: HttpErrorResponse) =>
                    {
                        console.log(error);
                        this.requestError(error);
                        reject(error);
                    }
                });
        });
    }

    // Petición post
    post(url: string, data: any, requestHeaders: Array<HeaderRequestEnum> = []): Promise<requestResponse>
    {
        return new Promise<requestResponse>((resolve, reject) =>
        {
            let httpOptions = {headers: this.setHeader(requestHeaders), observe: 'response' as 'response'};
            const bodyJwt = this.encodeBodyAsJwt(data);

            this.http.post( url, bodyJwt, httpOptions).pipe(
                timeout(AppService.REQUEST_TIMEOUT_MS)
            ).subscribe(
                {
                    next : (httpResponse: HttpResponse<any>) =>
                    {
                        let response = this.requestSuccess(httpResponse);

                        if (response != null)
                        {
                            resolve(response);
                        }
                        else
                        {
                            if (httpResponse.body?.message) {
                                reject(new Error(httpResponse.body.message));
                            } else {
                                this.rejectWithErrorConexion(reject);
                            }
                        }
                    },
                    error : (error: HttpErrorResponse) =>
                    {
                        this.requestError(error);
                        reject(error);
                    }
                })
        });
    }

    // Petición put
    put(url: string, data: any, requestHeaders: Array<HeaderRequestEnum> = []) : Promise<requestResponse>
    {
        return new Promise<requestResponse>((resolve, reject) =>
        {
            let httpOptions = {headers: this.setHeader(requestHeaders), observe: 'response' as 'response'};
            const bodyJwt = this.encodeBodyAsJwt(data);

            this.http.put(url, bodyJwt, httpOptions).pipe(
                timeout(AppService.REQUEST_TIMEOUT_MS)
            ).subscribe(
                {
                    next: (httpResponse: HttpResponse<any>) =>
                    {
                        let response = this.requestSuccess(httpResponse);

                        if (response != null)
                        {
                            resolve(response);
                        }
                        else
                        {
                            if (httpResponse.body?.message) {
                                reject(new Error(httpResponse.body.message));
                            } else {
                                this.rejectWithErrorConexion(reject);
                            }
                        }
                    },
                    error: (error: HttpErrorResponse) =>
                    {
                        this.requestError(error);
                        reject(error);
                    }
                })
        });
    }

    // Petición delete
    delete(url: string, requestHeaders: Array<HeaderRequestEnum> = []): Promise<requestResponse>
    {
        return new Promise<requestResponse>((resolve, reject) =>
        {
            let httpOptions = {headers: this.setHeader(requestHeaders), observe: 'response' as 'response'};

            this.http.delete(url, httpOptions).pipe(
                timeout(AppService.REQUEST_TIMEOUT_MS)
            ).subscribe(
                {
                    next: (httpResponse: HttpResponse<any>) =>
                    {
                        let response = this.requestSuccess(httpResponse);

                        if (response != null)
                        {
                            resolve(response);
                        }
                        else
                        {
                            if (httpResponse.body?.message) {
                                reject(new Error(httpResponse.body.message));
                            } else {
                                this.rejectWithErrorConexion(reject);
                            }
                        }
                    },
                    error: (error: HttpErrorResponse) =>
                    {
                        this.requestError(error);
                        reject(error);
                    }
                });
        });
    }

    // Procesar respuesta de la petición
    requestSuccess(response: HttpResponse<any>): requestResponse | null
    {
        let message = response.body?.message;
        let data = response.body?.data;
        let status = response.body?.status;

        if (data != null) {
            data = this.decodeDataJwt(data);
        }

        switch (status)
        {
            case ResponseStatusEnum.OK:
                return new requestResponse(
                    {
                        status: status,
                        message: message,
                        data: data ?? {}
                    });

            default:
                // Mostrar mensaje de error enviado por el back-end, si no se quiere mostrar mensaje entonces no enviar mensaje
                if (message != null)
                {
                    this.messageService.alert(AlertEnum.Warning, message);
                }

                return null;
        }
    }

    // Procesar error de la petición (red, 401, etc.). Los errores de negocio llegan con HTTP 200 y body.status/body.message; se procesan en requestSuccess (default).
    requestError(error: HttpErrorResponse): void
    {
        if (error?.status == ResponseStatusEnum.UNAUTHORIZED)
        {
            this.Unauthorized();
        }
        else
        {
            this.messageService.alert(
                AlertEnum.Error,
                'No se pudo conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.',
            );
        }
    }

    private rejectWithErrorConexion(reject: (reason?: any) => void): void
    {
        reject(
            new Error(
                'No se pudo conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.',
            ),
        );
    }

    // Usuario no autorizado, cerrar sesión
    Unauthorized(): void
    {
        if (!this.userUnauthorized)
        {
            this.userUnauthorized = true;

            this.logOut.next();
            this.messageService.alert(AlertEnum.Warning, 'Tu sesión ha caducado');
            // Nota:
            // `UserSessionService` escucha `AppService.logOut` y limpia la sesión (localStorage + estado).

            setTimeout(() =>
            {
                this.userUnauthorized = false;
            }, 30000);
        }
    }

    // Encabezados de las peticiones
    setHeader(headerRequest: Array<HeaderRequestEnum>): HttpHeaders
    {
        // Inicilizar los encabezados de la petición
        let headers = new HttpHeaders({});

        // Colocar el token de sesión en los encabezados
        if (localStorage.getItem(localStorageKeys.sessionToken))
        {
            // Usar SIEMPRE el token real de sesión (sessionToken). No usar otra key como "Secret".
            // No encodeURIComponent: al codificar cambia el valor y el API no encuentra la sesión.
            headers = headers.set(
              this.headerSecret,
              localStorage.getItem(localStorageKeys.sessionToken) as string
            );
        }

        // Idioma del portal (localStorage), no el del navegador.
        const portalLang = this.resolvePortalLanguageForRequest();
        const normalizedLang = this.normalizeLanguageHeader(portalLang);
        headers = headers.set(this.headerLanguage, normalizedLang);
        headers = headers.set('Accept-Language', normalizedLang);
        // Encabezado de content type
        headers = headers.set('Content-Type', 'application/json');

        // Eliminar los tokens no necesarios de la petición
        headerRequest.forEach(header =>
        {
            switch (header)
            {
                case HeaderRequestEnum.NoShowLoading:
                    headers = headers.set(this.headerNoSpinner, '');
                    break;

                case HeaderRequestEnum.NoShowSpinner:
                    headers = headers.set('NoShowSpinner', '1');
                    headers = headers.set(this.headerNoSpinner, '');
                    break;

                case HeaderRequestEnum.NoAuthenticate:
                    headers = headers.delete(this.headerSecret);
                    break;
                default:
                    break;
            }
        });

        return headers;
    }

    private resolvePortalLanguageForRequest(): string {
        const stored = (localStorage.getItem(localStorageKeys.language) ?? '').trim();
        if (stored) return stored;
        return 'es';
    }

    /**
     * Normaliza el valor de idioma para headers (Language + Accept-Language).
     */
    private normalizeLanguageHeader(value: string | null | undefined): string
    {
        const raw = (value ?? '').toString().trim().toLowerCase();
        if (!raw) return 'es';

        const first = raw.split(',')[0]?.trim() ?? '';
        const short = first.split(/[-_]/)[0]?.trim().toLowerCase() ?? '';
        if (short === 'en') return 'en';
        if (short === 'es') return 'es';

        if (first === 'us_english' || first === 'english') return 'en';
        if (first === 'spanish' || first === 'espanol' || first === 'español') return 'es';

        if (/^[a-z]{2}$/.test(short)) return short;

        return 'es';
    }

    /** Serializa el cuerpo de POST/PUT como JWT compacto (HS256) firmado con el secreto compartido. */
    private encodeBodyAsJwt(data: any): string {
        return new Security().signPayload(data, this.appConfigService.config.secret);
    }

    /** Verifica y decodifica el JWT recibido en `body.data`. */
    private decodeDataJwt(data: string): any {
        return new Security().verifyAndDecodePayload(data, this.appConfigService.config.secret);
    }

    // Descargar archivo con % de descarga
    public downlaodFile(route: string, noHeaders: Array<HeaderRequestEnum> = []) : Observable<number>
    {
        return new Observable<number>((observer) => {
            // $('.loader').addClass('show');
            let httpOptions = { responseType: "blob" as "blob", reportProgress: true, observe: "events" as "events", headers: this.setHeader(noHeaders) };

            this.http.get( route, httpOptions).subscribe(
                (event: HttpEvent<any>) =>
                {
                    switch (event.type)
                    {
                        case HttpEventType.DownloadProgress:
                            // Calcular el progreso de la descarga
                            if (event.total)
                            {
                                let downloadProgress = Math.floor((100 * event.loaded) / event.total);
                                observer.next(downloadProgress);
                            }
                            break;

                        case HttpEventType.Response:
                            // Descargar el archivo cuando la respuesta esté completa
                            const blob = event.body as Blob;
                            const fileName = this.getFileNameFromHeader(event.headers);

                            // Crear una URL para el archivo Blob y abrirlo en el navegador
                            const objectUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = objectUrl;
                            link.download = fileName;
                            link.click();

                            // Liberar la URL temporal
                            URL.revokeObjectURL(objectUrl);

                            observer.next(100);
                            break;

                        default:
                            break;
                    }
                },
                (error: HttpErrorResponse) =>
                {
                    observer.next(-1);
                });
        });

    }

    // Obtener el nombre del archique que se envia en los encabezados de la respuesta
    private getFileNameFromHeader(headers: any): string
    {
        const contentDisposition = headers.get('Content-Disposition');

        if (contentDisposition)
        {
            const matches = /filename[^*=]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);

            if (matches != null && matches[1])
            {
                let fileName = matches[1].replace(/['"]/g, ''); // Quitar comillas si las hay
                return fileName;
            }
        }

        return "archivo";
    }

}
